import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { withTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { useGetActiveRooms } from '@core/dashboard/components/EventDrawer/EventDrawerContent/ParticipantsTab/hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import SearchField, {
  useSearchField,
} from '@core/dashboard/components/SearchField';
import ScrollableContainer from '@components/ScrollableContainer';
import { IParticipant } from '@interfaces/participants';
import { isScheduledEvent } from '@helpers/events';
import ParticipantList from './ParticipantList';
import ParticipantNumberInfo from './ParticipantNumberInfo';
import { useStyles } from './styles';
import { IParticipantsContent } from './interfaces';
import { useSearchUserContext } from '@providers/SearchUserProvider';

const ParticipantsContent = ({
  onClose,
  t: translate,
}: IParticipantsContent) => {
  const { focusOnSearchField, setSearchResultNumber } = useSearchUserContext();
  const classes = useStyles();

  const [page, setPage] = useState(1);
  const bottomRef = useRef(null);
  const isBottomVisibleRef = useRef(true);

  const { rooms, participants, currentParticipant, currentEvent } =
    useMytaverseEvent();

  const fieldsForFilter = useMemo(() => ['name', 'email'], []);

  const { inputValue, filteredData, handleInputChange, handleInputClear } =
    useSearchField<IParticipant>(participants, fieldsForFilter);

  const getActiveRooms = useGetActiveRooms();

  const activeRooms = useMemo(
    () => getActiveRooms(filteredData, rooms),
    [filteredData, rooms, getActiveRooms],
  );

  const sortParticipantsByRaisedHand = useCallback(
    (participants: IParticipant[]) => {
      const raisedHandParticipants = participants.filter((p) => p.handRaised);
      const notRaisedHandParticipants = participants.filter(
        (p) => !p.handRaised,
      );

      // Sort raised hand participants by handRaisedAt timestamp (earliest first)
      raisedHandParticipants.sort((a, b) => {
        const aTime = a.handRaisedAt || 0;
        const bTime = b.handRaisedAt || 0;
        return aTime - bTime;
      });

      return [...raisedHandParticipants, ...notRaisedHandParticipants];
    },
    [],
  );

  const participantsList = useMemo(
    () =>
      activeRooms.slice().map((r) => {
        if (r.roomId === 'offline') {
          const activeParticipants = r.activeParticipants.slice(0, 20 * page);
          isBottomVisibleRef.current =
            activeParticipants.length < r.activeParticipants.length;
          return {
            ...r,
            activeParticipants:
              sortParticipantsByRaisedHand(activeParticipants),
          };
        }

        return {
          ...r,
          activeParticipants: sortParticipantsByRaisedHand(
            r.activeParticipants,
          ),
        };
      }),
    [activeRooms, page, sortParticipantsByRaisedHand],
  );

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((prevPage) => ++prevPage);
      }
    });

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => {
      if (bottomRef.current) observer.unobserve(bottomRef.current);
    };
  }, []);

  useEffect(() => {
    setSearchResultNumber(filteredData.length);
  }, [filteredData, setSearchResultNumber]);

  return (
    <>
      {!isMobileOnly && currentEvent && isScheduledEvent(currentEvent) && (
        <ParticipantNumberInfo />
      )}
      {(!isMobileOnly || focusOnSearchField) && (
        <SearchField
          value={inputValue}
          onChange={handleInputChange}
          onClear={handleInputClear}
          placeholder={translate('inputPlaceholders.sidebarParticipant')}
          focusOnSearchField={focusOnSearchField}
        />
      )}

      <ScrollableContainer sxProps={{ root: classes.scrollable }}>
        {!filteredData.length ? (
          <Typography sx={classes.empty}>
            {translate('participantsModal.searchNoResults')} {inputValue}
          </Typography>
        ) : null}
        {participantsList.map((room) => (
          <ParticipantList
            key={room.roomId}
            name={room.roomId === 'offline' ? translate('sidebar.offline') : ''}
            participants={room.activeParticipants}
            currentParticipantId={currentParticipant?.participantId || ''}
            isOffline={room.roomId === 'offline'}
            onClose={onClose}
          />
        ))}

        {isBottomVisibleRef.current ? (
          <div ref={bottomRef} style={{ height: '4px' }}></div>
        ) : null}
      </ScrollableContainer>
    </>
  );
};

export default withTranslation()(ParticipantsContent);
